import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

// Custom validator to check that two fields match
export function MustMatch(
  controlName: string,
  matchingControlName: string
): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const control = formGroup.get(controlName);
    const matchingControl = formGroup.get(matchingControlName);

    // Check if both controls are available
    if (!control || !matchingControl) {
      return null;
    }

    // If matchingControl has errors, don't override them
    if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
      return null;
    }

    // If the values don't match, set the error on the matchingControl
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
    } else {
      matchingControl.setErrors(null); // clear error if values match
    }

    return null;
  };
}

// Password strength pattern: at least one uppercase letter, one digit, and one special character, with a minimum length of 12
export function PasswordStrengthValidator(): ValidatorFn {
  const passwordPattern =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?~`-])[A-Za-z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?~`-]{12,}$/;

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // don't validate empty values
    }

    const isValid = passwordPattern.test(control.value);
    return isValid ? null : { weakPassword: true };
  };
}

// Updated custom validator to set error on newPassword control instead of form level
export function oldAndNewPasswordShouldNotMatch(group: FormGroup) {
  const oldPassword = group.get('oldPassword')?.value;
  const newPassword = group.get('newPassword')?.value;
  const newPasswordControl = group.get('newPassword');

  if (oldPassword && newPassword && oldPassword === newPassword) {
    // Set error on the newPassword control instead of form level
    newPasswordControl?.setErrors({
      ...newPasswordControl.errors,
      passwordSameAsOld: true,
    });
    return { passwordSameAsOld: true }; // Still return form-level error for consistency
  } else {
    // Clear the passwordSameAsOld error if passwords are different
    if (newPasswordControl?.errors?.['passwordSameAsOld']) {
      const errors = { ...newPasswordControl.errors };
      delete errors['passwordSameAsOld'];
      newPasswordControl.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  return null;
}

//validator to check if a control contains valid comma-separated emails
export function multipleEmailValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value as string;

    if (!value) {
      return null;
    }

    const emails = value
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const invalidEmail = emails.find((email) => !emailPattern.test(email));

    return invalidEmail ? { invalidEmail: invalidEmail } : null;
  };
}
